/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { getDefaultPieveAppSettings } from '@/lib/pieveAppSettings.js';
import inferMemberDisplayNameFromEmail from '@/lib/settings/members/inferMemberDisplayNameFromEmail.js';
import resolveDefaultMemberRoleOption from '@/lib/settings/members/resolveDefaultMemberRoleOption.js';
import sortMemberItems from '@/lib/settings/members/sortMemberItems.js';
import sortRoleOptions from '@/lib/settings/members/sortRoleOptions.js';
import { getPievePrefKeyFromSettingPath, PIEVE_LOCAL_ONLY_SETTING_PATHS } from '@/lib/prefs/pieveSettingsPrefsMap.js';
import getRoleAssignmentCounts from '@/lib/settings/rolePermissions/getRoleAssignmentCounts.js';
import normalizeRoleCollection from '@/lib/settings/rolePermissions/normalizeRoleCollection.js';
import normalizeRoleRecord from '@/lib/settings/rolePermissions/normalizeRoleRecord.js';
import * as uiSelectors from '@/redux/selectors/uiSelectors.js';
import * as userSelectors from '@/redux/selectors/userSelectors.js';
import { toSafeString } from '@shared/utils';
import { COMPANY_LINK_REQUEST_SIDE } from '@shared/schema/companyParent.js';
import canManageOrganizationLinkSide from '@/lib/settings/organizationLinks/canManageOrganizationLinkSide.js';

const DEFAULT_SETTINGS = Object.freeze(getDefaultPieveAppSettings());
const LOCAL_ONLY_SETTING_KEY_SET = new Set(PIEVE_LOCAL_ONLY_SETTING_PATHS);
const EMPTY_SYNCED_SETTINGS = Object.freeze({});
const EMPTY_ARRAY = Object.freeze([]);

export const settingsState = (state) => state?.settings || null;
export const localSettings = (state) => settingsState(state)?.local || null;
export const openDestinationOptions = (state) => settingsState(state)?.openDestinationOptions || EMPTY_ARRAY;
export const organizationParentLinks = (state) => settingsState(state)?.organizationParentLinks || null;
export const organizationParent = (state) => organizationParentLinks(state)?.parent || null;
export const organizationChildren = (state) => organizationParentLinks(state)?.children || EMPTY_ARRAY;
export const organizationLinkHistory = (state) => organizationParentLinks(state)?.history || EMPTY_ARRAY;
export const organizationMembersAddedByParent = (state) => organizationParentLinks(state)?.membersAddedByParent || EMPTY_ARRAY;
export const canManageChildOrganizations = (state) => organizationParentLinks(state)?.capabilities?.canManageChildren === true;
export const canManageParentOrganization = (state) => organizationParentLinks(state)?.capabilities?.canManageParent === true;
/* Every pending request, with the role this organization plays in it (`side`) and whether the
   viewer holds that side's permission (`canAct`): answering an incoming request and cancelling an
   outgoing one both need it, server side too. */
export const organizationLinkRequests = createSelector([organizationParentLinks], (links) => {
  return (links?.requests || EMPTY_ARRAY).map((request) => {
    const side = request.parentCompany.id === links.companyId ? COMPANY_LINK_REQUEST_SIDE.PARENT : COMPANY_LINK_REQUEST_SIDE.CHILD;

    return { ...request, side, canAct: canManageOrganizationLinkSide({ side, capabilities: links.capabilities }) };
  });
});
export const organizationLinkRequest = createSelector([organizationLinkRequests, (_state, requestId) => requestId], (requests, requestId) => requests.find((request) => request.id === requestId) || null);
export const organizationChild = createSelector([organizationChildren, (_state, childCompanyId) => childCompanyId], (children, childCompanyId) => children.find((child) => child.id === childCompanyId) || null);
export const incomingOrganizationLinkRequests = createSelector([organizationLinkRequests], (requests) => requests.filter((request) => request.direction === 'incoming'));
export const outgoingOrganizationLinkRequests = createSelector([organizationLinkRequests], (requests) => requests.filter((request) => request.direction === 'outgoing'));
/* The roles this organization may ask to take in a new link, in the order the form offers them. */
export const organizationLinkableSides = createSelector([organizationParentLinks], (links) => {
  return [COMPANY_LINK_REQUEST_SIDE.PARENT, COMPANY_LINK_REQUEST_SIDE.CHILD].filter((side) => canManageOrganizationLinkSide({ side, capabilities: links?.capabilities }));
});
export const apiKeys = (state) => settingsState(state)?.apiKeys ?? null;
/* The keys the server listed, active ones first; null until the list has been read. The server
   decides whose keys these are (the viewer's own, or everyone's with api_key:manage, staff-created
   keys included), so nothing is filtered here. */
export const visibleApiKeys = createSelector([apiKeys], (keys) => {
  if (!keys) {
    return null;
  }

  return [...keys.filter((key) => !key.revokedOn), ...keys.filter((key) => key.revokedOn)];
});
export const apiKey = createSelector([apiKeys, (_state, keyId) => keyId], (keys, keyId) => (keys || EMPTY_ARRAY).find((key) => key.id === keyId) || null);
export const apiKeyCreatorSummary = (state, userId) => settingsState(state)?.apiKeyCreatorSummariesByUserId?.[userId] || null;
export const rolePermissionRoles = (state) => settingsState(state)?.roles || EMPTY_ARRAY;
export const rolePermissionMembers = (state) => settingsState(state)?.members || EMPTY_ARRAY;
export const rolePermissionCatalog = (state) => settingsState(state)?.permissionCatalog || EMPTY_ARRAY;
export const rolePermissionGrants = (state) => settingsState(state)?.rolePermissionGrants ?? null;
export const rolePermissionRoleCapabilitiesByRoleId = (state) => settingsState(state)?.roleCapabilitiesByRoleId || EMPTY_SYNCED_SETTINGS;
export const rolePermissionRoleCapabilityServerPayloadByRoleId = (state) => settingsState(state)?.roleCapabilityServerPayloadByRoleId || EMPTY_SYNCED_SETTINGS;
export const rolePermissionRoleAppsByRoleId = (state) => settingsState(state)?.roleAppsByRoleId || EMPTY_SYNCED_SETTINGS;
export const rolePermissionRoleAppModelsByRoleId = (state) => settingsState(state)?.roleAppModelsByRoleId || EMPTY_SYNCED_SETTINGS;
export const userCapabilityPayload = (state) => settingsState(state)?.userCapabilityPayload || null;
export const userCapabilityServerPayload = (state) => settingsState(state)?.userCapabilityServerPayload || null;
export const userCapabilityAppsPayload = (state) => settingsState(state)?.userCapabilityAppsPayload || null;
export const userCapabilityAppModelsByAppId = (state) => settingsState(state)?.userCapabilityAppModelsByAppId || EMPTY_SYNCED_SETTINGS;
export const isSettingsLoading = (state) => uiSelectors.isSettingsLoading(state);
export const isSettingsSaving = (state) => uiSelectors.isSettingsSaving(state);
export const settingsError = (state) => uiSelectors.settingsError(state);
export const hasResolvedInitialSettings = (state) => uiSelectors.hasResolvedInitialSettings(state);
export const syncedSettings = (state) => uiSelectors.pref(state, 'pieve.settings') || EMPTY_SYNCED_SETTINGS;
export const rolePermissionNormalizedRoles = createSelector([rolePermissionRoles, userSelectors.activeCompanyId], (roles, activeCompanyId) => {
  return normalizeRoleCollection({
    roles,
    activeCompanyId
  });
});
export const rolePermissionRolesById = createSelector([rolePermissionNormalizedRoles], (normalizedRoles) => {
  return normalizedRoles.all.reduce((accumulator, role) => {
    accumulator[role.id] = role;
    return accumulator;
  }, {});
});
export const rolePermissionRoleAssignmentCounts = createSelector([rolePermissionMembers], (members) => {
  return getRoleAssignmentCounts({
    members
  });
});
export const rolePermissionRoleOptions = createSelector([rolePermissionRoles], (roles) => {
  return sortRoleOptions((Array.isArray(roles) ? roles : []).map((role) => normalizeRoleRecord(role)).filter((role) => Boolean(role.id) && Boolean(role.name)));
});
/* The roles the current user may assign: every permission the role grants is one they hold, the
   same grant ceiling the API enforces on invites and role changes. Grant rows carry the permission key. */
export const rolePermissionAssignableRoleOptions = createSelector([rolePermissionRoleOptions, rolePermissionGrants, userSelectors.authPermissions], (roleOptions, grants, held) => {
  const heldKeys = new Set(held);
  const beyondUser = new Set((grants || EMPTY_ARRAY).filter((grant) => !heldKeys.has(grant.permissionKey)).map((grant) => grant.roleId));
  return roleOptions.filter((role) => !beyondUser.has(role.id));
});
export const rolePermissionDefaultMemberRoleOption = createSelector([rolePermissionAssignableRoleOptions], (roleOptions) => {
  return resolveDefaultMemberRoleOption(roleOptions);
});
export const rolePermissionRoleNameById = createSelector([rolePermissionRoleOptions], (roleOptions) => {
  return roleOptions.reduce((accumulator, role) => {
    accumulator[role.id] = role.name;
    return accumulator;
  }, {});
});
export const rolePermissionMemberItems = createSelector([rolePermissionMembers, rolePermissionRoleNameById], (members, roleNameById) => {
  return sortMemberItems(
    (Array.isArray(members) ? members : [])
      .map((member) => {
        const rawName = toSafeString(member?.name);
        const normalizedName = ['null', 'undefined'].includes(rawName.toLowerCase()) ? '' : rawName;
        const roleLabel = toSafeString(member?.roleName) || roleNameById?.[member?.roleId] || 'Unknown role';
        const inferredName = inferMemberDisplayNameFromEmail(member?.email);
        const displayName = normalizedName || inferredName || 'Member';

        return {
          ...member,
          displayName,
          roleLabel
        };
      })
      .filter((member) => Boolean(member.email) && member?.isAdmin !== true)
  );
});
export const rolePermissionRoleAppsPayload = (state, roleId) => {
  const normalizedRoleId = toSafeString(roleId);

  if (!normalizedRoleId) {
    return null;
  }

  return rolePermissionRoleAppsByRoleId(state)?.[normalizedRoleId] || null;
};
export const rolePermissionRoleCapabilitiesPayload = (state, roleId) => {
  const normalizedRoleId = toSafeString(roleId);

  if (!normalizedRoleId) {
    return null;
  }

  return rolePermissionRoleCapabilitiesByRoleId(state)?.[normalizedRoleId] || null;
};
export const rolePermissionRoleCapabilityServerPayload = (state, roleId) => {
  const normalizedRoleId = toSafeString(roleId);

  if (!normalizedRoleId) {
    return null;
  }

  return rolePermissionRoleCapabilityServerPayloadByRoleId(state)?.[normalizedRoleId] || null;
};
export const rolePermissionRoleAppModelsPayload = (state, roleId, appId) => {
  const normalizedRoleId = toSafeString(roleId);
  const normalizedAppId = toSafeString(appId);

  if (!normalizedRoleId || !normalizedAppId) {
    return null;
  }

  return rolePermissionRoleAppModelsByRoleId(state)?.[normalizedRoleId]?.[normalizedAppId] || null;
};
export const userCapabilityAppModelsPayload = (state, appId) => {
  const normalizedAppId = toSafeString(appId);

  if (!normalizedAppId) {
    return null;
  }

  return userCapabilityAppModelsByAppId(state)?.[normalizedAppId] || null;
};
export const featureSettingExplicitState = createSelector([syncedSettings], (currentSyncedSettings) => {
  const hasExplicitSetting = (settingKey) => {
    return Object.prototype.hasOwnProperty.call(currentSyncedSettings || {}, settingKey);
  };

  return {
    enableCodeMode: hasExplicitSetting('enableCodeMode'),
    allowProjectFolders: hasExplicitSetting('allowProjectFolders'),
    enableTerminal: hasExplicitSetting('enableTerminal'),
    allowAgentToUsePython: hasExplicitSetting('allowAgentToUsePython'),
    allowAgentToUseGit: hasExplicitSetting('allowAgentToUseGit')
  };
});
export const localSettingValue = (state, key) => {
  const normalizedKey = String(key || '').trim();

  if (!normalizedKey) {
    return undefined;
  }

  if (!LOCAL_ONLY_SETTING_KEY_SET.has(normalizedKey)) {
    return undefined;
  }

  const localValue = localSettings(state)?.[normalizedKey];

  if (localValue !== undefined) {
    return localValue;
  }

  return DEFAULT_SETTINGS[normalizedKey];
};

export const defaultOpenDestinationSetting = (state) => localSettingValue(state, 'defaultOpenDestination');
export const preventSleepWhileRunningSetting = (state) => localSettingValue(state, 'preventSleepWhileRunning');
export const useOpaqueWindowBackgroundSetting = (state) => localSettingValue(state, 'useOpaqueWindowBackground');
export const sansFontFamilySetting = (state) => localSettingValue(state, 'sansFontFamily');
export const codeFontFamilySetting = (state) => localSettingValue(state, 'codeFontFamily');

export const settingValue = (state, key) => {
  const normalizedKey = String(key || '').trim();

  if (!normalizedKey) {
    return undefined;
  }

  if (LOCAL_ONLY_SETTING_KEY_SET.has(normalizedKey)) {
    return localSettingValue(state, normalizedKey);
  }

  const prefKey = getPievePrefKeyFromSettingPath(normalizedKey);

  if (!prefKey) {
    return DEFAULT_SETTINGS[normalizedKey];
  }

  const prefValue = uiSelectors.pref(state, prefKey);

  if (prefValue !== undefined) {
    return prefValue;
  }

  return DEFAULT_SETTINGS[normalizedKey];
};
